import React from 'react';
import { View } from 'react-native';
import { styled } from 'dripsy';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation, useRoute } from '@react-navigation/native';

import { BaseText, Image, Button } from '@components';
import { useViewerModel } from '@models/Viewer/ViewerModel';
import { IS_MOBILE, IS_WEB, parseApiError } from '@utils';
import {
  useAsyncInit,
  useFocusStatusBarOneTimeLight,
  useNavOptionsSet,
  usePersonalizationNavigation,
} from '@hooks';
import {
  PersonalizationNavigatorNavigationPropType,
  PersonalizationNavigatorRoutePropType,
} from '@navigation';
import { ToastService } from '@services';
import { useAuthModel } from '@models/AuthModel';

const Container = styled(View)({
  flex: 1,
  justifyContent: IS_WEB ? 'center' : 'flex-end',
  alignItems: [null, 'center'],
  backgroundColor: '$background0',
});

interface IContentContainerStyles {
  bottom: number;
}

const ContentContainer = styled(View)(
  (props: IContentContainerStyles) => ({
    bottom: 32 + props.bottom,
    px: '$6',
    maxWidth: [null, 400],
  }),
);

const BackgroundImage = styled(Image)({
  position: 'absolute',
  width: '100%',
  height: '100%',
});

const Title = styled(BaseText)({
  variant: IS_WEB ? 'text.xbig' : 'text.xxlarge',
  color: IS_WEB ? '$heading800' : '$background0',
  textAlign: 'center',
  mb: '$5',
  fontWeight: 'semi',
});

const Text = styled(BaseText)({
  variant: 'text.small',
  color: IS_WEB ? '$text90' : '$background10',
  textAlign: 'center',
});

const ButtonContainer = styled(View)({
  pt: '$8',
});

type NavigationPropType =
  PersonalizationNavigatorNavigationPropType<'SuccessfulSignIn'>;

type RoutePropType =
  PersonalizationNavigatorRoutePropType<'SuccessfulSignIn'>;

export const SuccessfulSignInScreen = observer(() => {
  const { t } = useTranslation();
  const auth = useAuthModel();
  const viewer = useViewerModel();
  const insets = useSafeAreaInsets();
  const { params } = useRoute<RoutePropType>();
  const nav = useNavigation<NavigationPropType>();
  const { navigateToNextScreen } = usePersonalizationNavigation();

  useFocusStatusBarOneTimeLight();

  useNavOptionsSet({ title: t('successfulSignIn.title') });

  useAsyncInit(async () => {
    if (typeof params?.inviteVideoId === 'undefined') {
      try {
        // If sign-up was made via invitation from a video tutorial, we can get
        // this video's id for future redirect to this video tutorial
        const data = await auth.getInviteVideo.run();

        if (typeof data?.id === 'number') {
          // We set inviteVideoId into navigation params in order to save this id
          // through page refreshing on the WEB. We use this id for future redirecting
          nav.setParams({
            ...(params ?? {}),
            inviteVideoId: data.id,
          });
        }
      } catch (error) {
        ToastService.showError(parseApiError(error).message);
      }
    }
  });

  const title = `${t('successfulSignIn.title')} ${
    viewer.currentUser?.firstName
  }!`;

  return (
    <Container>
      {IS_MOBILE && (
        <BackgroundImage
          source={require('../../../assets/images/personalization.jpg')}
        />
      )}
      <ContentContainer bottom={insets.bottom}>
        <Title>{title}</Title>
        <Text>{t('successfulSignIn.text')}</Text>
        <ButtonContainer>
          <Button onPress={navigateToNextScreen}>
            {t('successfulSignIn.letsStart')}
          </Button>
        </ButtonContainer>
      </ContentContainer>
    </Container>
  );
});
